import { enableMapSet } from 'immer';
import { lazy, Suspense, useState } from 'react';
import { createRoot } from 'react-dom/client';

import * as BuildConfig from 'soapbox/build-config.ts';
import StagingAdminGate from 'soapbox/features/staging-admin-gate/staging-admin-gate.tsx';
import { printConsoleWarning } from 'soapbox/utils/console.ts';
import {
  hasStagingAdminAuthorization,
  shouldRequireStagingAdminGate,
} from 'soapbox/utils/staging-admin-gate.ts';

import '@fontsource/inter/200.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import '@fontsource/inter/900.css';
import '@fontsource/roboto-mono/400.css';
import 'line-awesome/dist/font-awesome-line-awesome/css/all.css';
import 'soapbox/features/nostr/keyring.ts';

import './iframe.ts';
import './styles/i18n/arabic.css';
import './styles/i18n/javanese.css';
import './styles/application.scss';
import './styles/tailwind.css';

import ready from './ready.ts';
import { unregisterSW } from './utils/sw.ts';

enableMapSet();

const Soapbox = lazy(() => import('soapbox/init/soapbox.tsx'));

const AppEntry = () => {
  const [isGateAuthorized, setIsGateAuthorized] = useState(
    () =>
      !shouldRequireStagingAdminGate() || hasStagingAdminAuthorization(),
  );

  if (!isGateAuthorized) {
    return (
      <StagingAdminGate onAuthorized={() => setIsGateAuthorized(true)} />
    );
  }

  return (
    <Suspense fallback={null}>
      <Soapbox />
    </Suspense>
  );
};

if (BuildConfig.NODE_ENV === 'production') {
  printConsoleWarning();
  // Disable SW to avoid stale worker/cache issues breaking chunk loads.
  void unregisterSW();
}

ready(() => {
  const container = document.getElementById('soapbox') as HTMLElement;
  const root = createRoot(container);

  root.render(<AppEntry />);
});
